import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiTenant, ApiError } from "../lib/api"

type Tab = "departments" | "holidays" | "allowances"

interface Department {
  id: string
  code: string
  name: string
  parentId: string | null
}

interface Holiday {
  id: string
  date: string
  name: string
  payMultiplier: string
  notes: string | null
}

interface AllowanceTemplate {
  id: string
  code: string
  name: string
  category: "allowance" | "deduction" | "reimbursement"
  defaultAmount: string | null
  taxable: boolean
  epfEligible: boolean
  notes: string | null
}

export function SettingsPage() {
  const { slug = "" } = useParams<{ slug: string }>()
  const [tab, setTab] = useState<Tab>("departments")

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: "departments", label: "Departments" },
    { key: "holidays", label: "Holidays" },
    { key: "allowances", label: "Allowance templates" },
  ]

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-xs text-slate-500 mt-1">
          Tenant <span className="font-mono">{slug}</span>
        </p>
      </div>

      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.key ? "border-brand-500 text-brand-700" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "departments" && <DepartmentsTab slug={slug} />}
      {tab === "holidays" && <HolidaysTab slug={slug} />}
      {tab === "allowances" && <AllowancesTab slug={slug} />}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Departments
// ────────────────────────────────────────────────────────────

function DepartmentsTab(props: { slug: string }) {
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ["departments", props.slug],
    queryFn: () => apiTenant<{ departments: Department[] }>(props.slug, "/departments"),
  })
  const [showAdd, setShowAdd] = useState(false)
  const [editTarget, setEditTarget] = useState<Department | null>(null)
  const [code, setCode] = useState("")
  const [name, setName] = useState("")
  const [parentId, setParentId] = useState<string>("")
  const [error, setError] = useState("")

  const depts = list.data?.departments ?? []
  const byId = new Map(depts.map((d) => [d.id, d]))

  const create = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, "/departments", {
        method: "POST",
        body: JSON.stringify({
          code: code.trim(),
          name: name.trim(),
          parentId: parentId || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["departments", props.slug] })
      setShowAdd(false)
      setCode("")
      setName("")
      setParentId("")
      setError("")
    },
    onError: (err) => {
      if (err instanceof ApiError && err.message === "duplicate_code") setError("That code is already taken.")
      else setError(err instanceof Error ? err.message : "Failed")
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!code.trim() || !name.trim()) {
      setError("Code and name are required.")
      return
    }
    setError("")
    create.mutate()
  }

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="font-medium">Departments</div>
          <div className="text-xs text-slate-500 mt-0.5">Used to group employee contracts. Codes are immutable once set; name and parent can be edited.</div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded"
        >
          + Add
        </button>
      </div>
      {list.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {!list.isLoading && depts.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">No departments yet.</div>
      )}
      {depts.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Code</th>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Parent</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {depts.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                <td className="px-4 py-2 font-mono text-xs">{d.code}</td>
                <td className="px-4 py-2">{d.name}</td>
                <td className="px-4 py-2 text-xs text-slate-500">
                  {d.parentId ? (byId.get(d.parentId)?.name ?? d.parentId) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => setEditTarget(d)} className="text-xs text-brand-600 hover:text-brand-800">
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <Modal title="Add department" onClose={() => setShowAdd(false)} onSubmit={handleSubmit} pending={create.isPending}>
          <Field label="Code *">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="form-input" placeholder="ENG / FIN" />
          </Field>
          <Field label="Name *">
            <input value={name} onChange={(e) => setName(e.target.value)} className="form-input" placeholder="Engineering" />
          </Field>
          <Field label="Parent (optional)">
            <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="form-input">
              <option value="">— top level —</option>
              {depts.map((d) => (
                <option key={d.id} value={d.id}>{d.code} · {d.name}</option>
              ))}
            </select>
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </Modal>
      )}

      {editTarget && (
        <EditDepartmentModal
          slug={props.slug}
          department={editTarget}
          allDepartments={depts}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["departments", props.slug] })
            setEditTarget(null)
          }}
        />
      )}
    </div>
  )
}

function EditDepartmentModal(props: {
  slug: string
  department: Department
  allDepartments: Department[]
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(props.department.name)
  const [parentId, setParentId] = useState<string>(props.department.parentId ?? "")
  const [error, setError] = useState("")

  // Filter out self + descendants from the parent picker so the user
  // can't accidentally create a cycle through the UI.
  const descendantIds = new Set<string>([props.department.id])
  let changed = true
  while (changed) {
    changed = false
    for (const d of props.allDepartments) {
      if (d.parentId && descendantIds.has(d.parentId) && !descendantIds.has(d.id)) {
        descendantIds.add(d.id)
        changed = true
      }
    }
  }
  const validParents = props.allDepartments.filter((d) => !descendantIds.has(d.id))

  const update = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, `/departments/${props.department.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: name.trim(), parentId: parentId || null }),
      }),
    onSuccess: props.onSaved,
    onError: (err) => setError(err instanceof Error ? err.message : "Failed"),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError("Name is required.")
      return
    }
    setError("")
    update.mutate()
  }

  return (
    <Modal title={`Edit ${props.department.code}`} onClose={props.onClose} onSubmit={handleSubmit} pending={update.isPending}>
      <Field label="Code">
        <input value={props.department.code} className="form-input" readOnly disabled />
      </Field>
      <Field label="Name *">
        <input value={name} onChange={(e) => setName(e.target.value)} className="form-input" />
      </Field>
      <Field label="Parent">
        <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="form-input">
          <option value="">— top level —</option>
          {validParents.map((d) => (
            <option key={d.id} value={d.id}>{d.code} · {d.name}</option>
          ))}
        </select>
      </Field>
      {error && <div className="text-sm text-red-600">{error}</div>}
    </Modal>
  )
}

function HolidaysTab(props: { slug: string }) {
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ["holidays", props.slug],
    queryFn: () => apiTenant<{ holidays: Holiday[] }>(props.slug, "/settings/holidays"),
  })
  const [showAdd, setShowAdd] = useState(false)
  const [date, setDate] = useState("")
  const [name, setName] = useState("")
  const [payMultiplier, setPayMultiplier] = useState("2.0")
  const [error, setError] = useState("")

  const create = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, "/settings/holidays", {
        method: "POST",
        body: JSON.stringify({ date, name: name.trim(), payMultiplier: Number(payMultiplier) }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["holidays", props.slug] })
      setShowAdd(false)
      setDate("")
      setName("")
      setPayMultiplier("2.0")
      setError("")
    },
    onError: (err) => {
      if (err instanceof ApiError && err.message === "duplicate_date") setError("A holiday is already recorded for that date.")
      else setError(err instanceof Error ? err.message : "Failed")
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiTenant(props.slug, `/settings/holidays/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["holidays", props.slug] }),
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!date || !name.trim()) {
      setError("Date and name are required.")
      return
    }
    setError("")
    create.mutate()
  }

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="font-medium">Public holidays</div>
          <div className="text-xs text-slate-500 mt-0.5">Hours worked on these dates trigger holiday OT at the multiplier rate.</div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded"
        >
          + Add
        </button>
      </div>
      {list.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {list.data && list.data.holidays.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">No holidays configured.</div>
      )}
      {list.data && list.data.holidays.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Date</th>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-right px-4 py-2 font-medium">Pay multiplier</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {list.data.holidays.map((h) => (
              <tr key={h.id} className="border-b border-slate-100 last:border-b-0">
                <td className="px-4 py-2 font-mono text-xs">{h.date.slice(0, 10)}</td>
                <td className="px-4 py-2">{h.name}</td>
                <td className="px-4 py-2 text-right font-mono">{Number(h.payMultiplier).toFixed(1)}×</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${h.name}"?`)) remove.mutate(h.id)
                    }}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <Modal title="Add holiday" onClose={() => setShowAdd(false)} onSubmit={handleSubmit} pending={create.isPending}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date *">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="form-input" />
            </Field>
            <Field label="Pay multiplier">
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="3"
                value={payMultiplier}
                onChange={(e) => setPayMultiplier(e.target.value)}
                className="form-input"
              />
            </Field>
          </div>
          <Field label="Name *">
            <input value={name} onChange={(e) => setName(e.target.value)} className="form-input" placeholder="Vesak Poya" />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </Modal>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Allowance templates
// ────────────────────────────────────────────────────────────

function AllowancesTab(props: { slug: string }) {
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ["allowance-templates", props.slug],
    queryFn: () => apiTenant<{ allowanceTemplates: AllowanceTemplate[] }>(props.slug, "/settings/allowance-templates"),
  })
  const [showAdd, setShowAdd] = useState(false)
  const [form, setForm] = useState({
    code: "",
    name: "",
    category: "allowance" as AllowanceTemplate["category"],
    defaultAmount: "",
    taxable: true,
    epfEligible: true,
  })
  const [error, setError] = useState("")

  const create = useMutation({
    mutationFn: () =>
      apiTenant(props.slug, "/settings/allowance-templates", {
        method: "POST",
        body: JSON.stringify({
          ...form,
          code: form.code.trim(),
          name: form.name.trim(),
          defaultAmount: form.defaultAmount ? Number(form.defaultAmount) : null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["allowance-templates", props.slug] })
      setShowAdd(false)
      setForm({ code: "", name: "", category: "allowance", defaultAmount: "", taxable: true, epfEligible: true })
      setError("")
    },
    onError: (err) => {
      if (err instanceof ApiError && err.message === "duplicate_code") setError("That code is already taken.")
      else setError(err instanceof Error ? err.message : "Failed")
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiTenant(props.slug, `/settings/allowance-templates/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["allowance-templates", props.slug] }),
  })
  const seed = useMutation({
    mutationFn: () => apiTenant<{ created: string[]; skipped: string[] }>(
      props.slug,
      "/settings/allowance-templates/seed-sl-defaults",
      { method: "POST", body: "{}" },
    ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["allowance-templates", props.slug] })
      const c = res.created.length, k = res.skipped.length
      alert(`Seeded SL defaults: ${c} created, ${k} already existed.`)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.code.trim() || !form.name.trim()) {
      setError("Code and name are required.")
      return
    }
    setError("")
    create.mutate()
  }

  return (
    <div className="bg-white border border-slate-200 rounded">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="font-medium">Allowance templates</div>
          <div className="text-xs text-slate-500 mt-0.5">Reusable component definitions for transport, meal, loan repayment, etc.</div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => seed.mutate()}
            disabled={seed.isPending}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-medium rounded"
            title="Idempotent: TRANSPORT_ALLOW, MEAL_ALLOW, FUEL_REIMB, PHONE_ALLOW, MEDICAL_REIMB, LOAN_REPAY, ADVANCE_RECOVERY"
          >
            {seed.isPending ? "Seeding…" : "Seed SL defaults"}
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium rounded"
          >
            + Add
          </button>
        </div>
      </div>
      {list.isLoading && <div className="p-6 text-sm text-slate-400">Loading…</div>}
      {list.data && list.data.allowanceTemplates.length === 0 && (
        <div className="p-8 text-center text-sm text-slate-400">No templates configured.</div>
      )}
      {list.data && list.data.allowanceTemplates.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Code</th>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Category</th>
              <th className="text-right px-4 py-2 font-medium">Default</th>
              <th className="text-center px-4 py-2 font-medium">Taxable</th>
              <th className="text-center px-4 py-2 font-medium">EPF eligible</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {list.data.allowanceTemplates.map((t) => (
              <tr key={t.id} className="border-b border-slate-100 last:border-b-0">
                <td className="px-4 py-2 font-mono text-xs">{t.code}</td>
                <td className="px-4 py-2">{t.name}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{t.category}</td>
                <td className="px-4 py-2 text-right font-mono">{t.defaultAmount ? Number(t.defaultAmount).toLocaleString() : "—"}</td>
                <td className="px-4 py-2 text-center">{t.taxable ? "✓" : "—"}</td>
                <td className="px-4 py-2 text-center">{t.epfEligible ? "✓" : "—"}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${t.name}"?`)) remove.mutate(t.id)
                    }}
                    className="text-xs text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showAdd && (
        <Modal title="Add allowance template" onClose={() => setShowAdd(false)} onSubmit={handleSubmit} pending={create.isPending}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Code *">
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="form-input"
                placeholder="TRANSPORT_ALLOW"
              />
            </Field>
            <Field label="Category *">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value as AllowanceTemplate["category"] })}
                className="form-input"
              >
                <option value="allowance">Allowance</option>
                <option value="deduction">Deduction</option>
                <option value="reimbursement">Reimbursement</option>
              </select>
            </Field>
          </div>
          <Field label="Name *">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="form-input" />
          </Field>
          <Field label="Default amount (optional)">
            <input
              type="number"
              step="0.01"
              value={form.defaultAmount}
              onChange={(e) => setForm({ ...form, defaultAmount: e.target.value })}
              className="form-input"
              placeholder="7500"
            />
          </Field>
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.taxable} onChange={(e) => setForm({ ...form, taxable: e.target.checked })} />
              Taxable (counts toward APIT)
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.epfEligible} onChange={(e) => setForm({ ...form, epfEligible: e.target.checked })} />
              EPF eligible
            </label>
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </Modal>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────
// Shared modal + field helpers
// ────────────────────────────────────────────────────────────

function Modal(props: {
  title: string
  onClose: () => void
  onSubmit: (e: React.FormEvent) => void
  pending: boolean
  children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <form onSubmit={props.onSubmit} className="bg-white rounded-lg w-full max-w-lg shadow-xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="font-semibold">{props.title}</div>
          <button type="button" onClick={props.onClose} className="text-slate-400 hover:text-slate-700">
            ×
          </button>
        </div>
        <div className="p-5 space-y-3">{props.children}</div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button type="button" onClick={props.onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900">
            Cancel
          </button>
          <button
            type="submit"
            disabled={props.pending}
            className="px-3 py-1.5 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
          >
            {props.pending ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{props.label}</div>
      {props.children}
    </label>
  )
}
